import { defineStore } from 'pinia'
import { sharedGetToken } from '@shared/utils/helper'
import { toUiMarketHistory } from '@shared/transformer/market'
import { getExchangeApi, getIndexerRestMarketChronosApi } from '@shared/service/exchange'
import { MARKETS_HISTORY_FETCH_PARAMS } from '@/app/data/market'
import {
  createRequestKey,
  CLIENT_REQUEST_KEYS,
  apiCallsRequestCache
} from '@/app/services/apiCallsRequestCache'
import { Duration } from '@/types/enums'
import type { SharedUiMarketHistory } from '@shared/types'
import type {
  TokenStatic,
  ExchangeParams,
  FeeDiscountSchedule,
  FeeDiscountAccountInfo
} from '@injectivelabs/sdk-ts'

type ExchangeStoreState = {
  params?: ExchangeParams
  feeDiscountSchedule?: FeeDiscountSchedule
  feeDiscountAccountInfo?: FeeDiscountAccountInfo
  marketsHistory: Record<Duration, SharedUiMarketHistory[]>
}

const initialStateFactory = (): ExchangeStoreState => ({
  params: undefined,
  feeDiscountSchedule: undefined,
  feeDiscountAccountInfo: undefined,
  marketsHistory: {
    [Duration.All]: [],
    [Duration.OneHour]: [],
    [Duration.SixHour]: [],
    [Duration.OneDay]: [],
    [Duration.OneWeek]: [],
    [Duration.OneMonth]: [],
    [Duration.OneYear]: [],
    [Duration.YearToDate]: []
  }
})

export const useExchangeStore = defineStore('exchange', {
  state: (): ExchangeStoreState => initialStateFactory(),

  actions: {
    setParams(params?: ExchangeParams) {
      this.params = params
    },

    setFeeDiscountSchedule(feeDiscountSchedule?: FeeDiscountSchedule) {
      this.feeDiscountSchedule = feeDiscountSchedule
    },

    setFeeDiscountAccountInfo(
      feeDiscountAccountInfo?: FeeDiscountAccountInfo
    ) {
      this.feeDiscountAccountInfo = feeDiscountAccountInfo
    },

    setMarketsHistory(marketsHistory: ExchangeStoreState['marketsHistory']) {
      this.marketsHistory = marketsHistory
    },

    setMarketsHistoryByDuration({
      duration,
      marketsHistory
    }: {
      duration: Duration
      marketsHistory: SharedUiMarketHistory[]
    }) {
      const exchangeStore = useExchangeStore()

      exchangeStore.setMarketsHistory({
        ...exchangeStore.marketsHistory,
        [duration]: marketsHistory
      })
    },

    async initFeeDiscounts() {
      const exchangeStore = useExchangeStore()

      await exchangeStore.fetchFeeDiscountAccountInfo()
    },

    async fetchParams() {
      const exchangeStore = useExchangeStore()

      const exchangeApi = await getExchangeApi()
      const params = await exchangeApi.fetchModuleParams()

      exchangeStore.setParams(params)
    },

    async fetchFeeDiscountSchedule() {
      const exchangeStore = useExchangeStore()

      const exchangeApi = await getExchangeApi()
      const feeDiscountSchedule = await exchangeApi.fetchFeeDiscountSchedule()

      if (feeDiscountSchedule) {
        const quoteTokenMeta = (await Promise.all(
          feeDiscountSchedule.quoteDenomsList.map(
            async (denom) => await sharedGetToken(denom)
          )
        )) as TokenStatic[]

        const feeDiscountScheduleWithToken = {
          ...feeDiscountSchedule,
          quoteTokenMeta
        } as FeeDiscountSchedule

        exchangeStore.setFeeDiscountSchedule({
          ...feeDiscountScheduleWithToken,
          tierInfosList: [
            {
              volume: '0',
              stakedAmount: '0',
              makerDiscountRate: '0',
              takerDiscountRate: '0'
            },
            ...feeDiscountScheduleWithToken.tierInfosList
          ]
        })
      }
    },

    async fetchFeeDiscountAccountInfo() {
      const exchangeStore = useExchangeStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const exchangeApi = await getExchangeApi()
      const feeDiscountAccountInfo = await apiCallsRequestCache.cached({
        key: createRequestKey(CLIENT_REQUEST_KEYS.feeDiscountAccountInfo, {
          address: sharedWalletStore.authZOrInjectiveAddress
        }),
        request: () =>
          exchangeApi.fetchFeeDiscountAccountInfo(
            sharedWalletStore.authZOrInjectiveAddress
          )
      })

      if (feeDiscountAccountInfo) {
        exchangeStore.setFeeDiscountAccountInfo(feeDiscountAccountInfo)
      }
    },

    async fetchMarketsHistory({
      refresh,
      duration,
      marketIds
    }: {
      refresh?: boolean
      duration: Duration
      marketIds: string[]
    }) {
      if (marketIds.length === 0) {
        return
      }

      const params = MARKETS_HISTORY_FETCH_PARAMS[duration]

      if (!params) {
        return
      }

      const exchangeStore = useExchangeStore()
      const cachedMarketIds = new Set(
        exchangeStore.marketsHistory[duration].map(
          (history) => history.marketId
        )
      )

      const marketIdsToFetch = refresh
        ? marketIds
        : marketIds.filter((marketId) => !cachedMarketIds.has(marketId))

      if (marketIdsToFetch.length === 0) {
        return
      }

      const indexerRestMarketChronosApi = await getIndexerRestMarketChronosApi()

      try {
        const requestKey = createRequestKey(CLIENT_REQUEST_KEYS.marketHistory, {
          duration,
          countback: params.countback,
          resolution: params.resolution,
          marketIds: [...marketIdsToFetch].sort()
        })
        const request = () =>
          indexerRestMarketChronosApi.fetchMarketsHistory({
            marketIds: marketIdsToFetch,
            countback: params.countback,
            resolution: params.resolution
          })
        const marketsHistory = refresh
          ? await apiCallsRequestCache.dedupe(requestKey, request)
          : await apiCallsRequestCache.cached({
              request,
              key: requestKey
            })

        const historyByMarketId = new Map(
          exchangeStore.marketsHistory[duration].map((history) => [
            history.marketId,
            history
          ])
        )

        for (const marketHistory of marketsHistory) {
          const history = toUiMarketHistory(marketHistory)

          historyByMarketId.set(history.marketId, history)
        }

        exchangeStore.setMarketsHistoryByDuration({
          duration,
          marketsHistory: Array.from(historyByMarketId.values())
        })
      } catch {
        //
      }
    },

    reset() {
      const exchangeStore = useExchangeStore()

      const { feeDiscountSchedule, feeDiscountAccountInfo } =
        initialStateFactory()

      exchangeStore.setFeeDiscountSchedule(feeDiscountSchedule)
      exchangeStore.setFeeDiscountAccountInfo(feeDiscountAccountInfo)
    }
  }
})
